import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, CheckCircle2, HelpCircle, BookOpen, Layers, Info, Check, X } from 'lucide-react';
import { LearningLesson } from '../../types';
import { Button } from '../UI/Button';
import { Badge } from '../UI/Badge';

interface LessonReaderProps {
  lesson: LearningLesson;
  isCompleted: boolean;
  onCompleteLesson: (lessonId: string, selectedAnswer?: string) => void;
  onNavigateLesson: (lessonId: string) => void;
  onBackToOverview: () => void;
  prevLesson: LearningLesson | null;
  nextLesson: LearningLesson | null;
}

export const LessonReader: React.FC<LessonReaderProps> = ({
  lesson,
  isCompleted,
  onCompleteLesson,
  onNavigateLesson,
  onBackToOverview,
  prevLesson,
  nextLesson,
}) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);

  const handleOptionSelect = (optionVal: string) => {
    setSelectedOption(optionVal);
    setShowFeedback(true);
    onCompleteLesson(lesson.id, optionVal);
  };

  const handleMarkRead = () => {
    onCompleteLesson(lesson.id);
  };

  return (
    <div className="animate-fade-in" style={{ maxWidth: '950px', margin: '0 auto', padding: '16px 12px 60px 12px' }}>
      {/* Back to Overview Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <Button variant="secondary" icon={<ArrowLeft size={16} />} onClick={onBackToOverview}>
          Back to Learning Path
        </Button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <Badge variant="lecture">Lecture {lesson.lecture}</Badge>
          <Badge variant="tool">Coverage: {lesson.coverage.toUpperCase()}</Badge>
          {isCompleted && (
            <Badge variant="complete">
              <CheckCircle2 size={13} style={{ marginRight: '4px' }} />
              ✓ Lesson Complete
            </Badge>
          )}
        </div>
      </div>

      {/* Main Lesson Content Card */}
      <div className="glass-panel" style={{ padding: '24px 18px', marginBottom: '24px' }}>
        <div style={{ fontSize: '13px', color: 'var(--accent-blue)', fontWeight: 700, textTransform: 'uppercase', marginBottom: '6px' }}>
          Lesson {lesson.lessonNumber}
        </div>
        <h1 style={{ fontSize: '28px', color: '#ffffff', fontWeight: 800, margin: '0 0 20px 0', letterSpacing: '-0.5px' }}>
          {lesson.title}
        </h1>

        {/* WHAT ARE WE LEARNING? */}
        <div style={{ background: 'rgba(2, 132, 199, 0.1)', border: '1px solid rgba(56, 189, 248, 0.25)', borderRadius: 'var(--radius-md)', padding: '18px 22px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 700, color: 'var(--accent-blue)', textTransform: 'uppercase', marginBottom: '6px' }}>
            <HelpCircle size={15} />
            <span>What are we learning?</span>
          </div>
          <div style={{ fontSize: '15px', color: 'var(--text-main)', fontWeight: 500, lineHeight: 1.6 }}>
            {lesson.whatAreWeLearning}
          </div>
        </div>

        {/* INSTRUCTOR-DERIVED EXPLANATION */}
        <div style={{ marginBottom: '28px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 700, color: '#ffffff', textTransform: 'uppercase', marginBottom: '10px' }}>
            <BookOpen size={16} color="var(--accent-blue)" />
            <span>Instructor Explanation (Source-Locked)</span>
          </div>
          <div
            style={{
              background: 'rgba(2, 6, 23, 0.6)',
              border: '1px solid var(--border-color)',
              borderRadius: 'var(--radius-md)',
              padding: '22px',
              fontSize: '15px',
              color: 'var(--text-main)',
              lineHeight: 1.8,
              whiteSpace: 'pre-line',
            }}
          >
            {lesson.instructorExplanation}
          </div>
        </div>

        {/* INSTRUCTOR EXAMPLES */}
        {lesson.instructorExamples && lesson.instructorExamples.length > 0 && (
          <div style={{ marginBottom: '28px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--accent-indigo)', textTransform: 'uppercase', marginBottom: '10px' }}>
              Examples Used by Instructor:
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {lesson.instructorExamples.map((ex, idx) => (
                <div
                  key={idx}
                  style={{
                    background: 'rgba(99, 102, 241, 0.15)',
                    border: '1px solid rgba(99, 102, 241, 0.3)',
                    color: '#a5b4fc',
                    fontSize: '13px',
                    fontWeight: 600,
                    padding: '8px 14px',
                    borderRadius: '20px',
                  }}
                >
                  • {ex}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* VISUAL SITE ARCHITECTURE DIAGRAM (Specifically for Lecture 4 Website Structure) */}
        {lesson.id === 'lesson-3-4' && (
          <div
            style={{
              background: 'rgba(2, 6, 23, 0.75)',
              border: '1px solid var(--accent-blue)',
              borderRadius: 'var(--radius-md)',
              padding: '24px',
              marginBottom: '28px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 700, color: 'var(--accent-blue)', textTransform: 'uppercase', marginBottom: '16px' }}>
              <Layers size={16} />
              <span>Local Business Website Page Architecture Tree</span>
            </div>

            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '13px', color: 'var(--text-main)', lineHeight: 1.8 }}>
              <div>🏢 HOMEPAGE (Brand Overview & NAP - Name, Address, Phone)</div>
              <div>├── 🛠️ SERVICE PAGES (Dedicated subpage for EVERY service offered)</div>
              <div>│ &nbsp; &nbsp; ├── Dental Implants Page</div>
              <div>│ &nbsp; &nbsp; ├── Dental Braces Page</div>
              <div>│ &nbsp; &nbsp; ├── Dental Scaling Page</div>
              <div>│ &nbsp; &nbsp; └── Root Canal Page</div>
              <div>├── 📍 LOCATION PAGES (Dedicated subpages for multi-location cities)</div>
              <div>│ &nbsp; &nbsp; ├── Miami Branch Location Page</div>
              <div>│ &nbsp; &nbsp; └── Fort Lauderdale Branch Location Page</div>
              <div>├── 📰 BLOG SECTION (Educational patient/client articles)</div>
              <div>└── 📜 FOOTER LEGAL PAGES (Privacy Policy, Terms of Use, Disclaimer, Feedback)</div>
            </div>
          </div>
        )}

        {/* FUTURE NOTICE (IF APPLICABLE) */}
        {lesson.futureNotice && (
          <div style={{ background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: 'var(--radius-sm)', padding: '14px 18px', color: 'var(--accent-yellow)', fontSize: '13px', marginBottom: '28px' }}>
            <div style={{ fontWeight: 700, marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Info size={14} />
              <span>Future Roadmap Topic:</span>
            </div>
            {lesson.futureNotice}
          </div>
        )}

        {/* SIMPLE SUMMARY */}
        <div style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.25)', borderRadius: 'var(--radius-md)', padding: '16px 20px', marginBottom: '28px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--accent-green)', textTransform: 'uppercase', marginBottom: '4px' }}>
            Key Concept Summary
          </div>
          <div style={{ fontSize: '14px', color: 'var(--text-main)', fontWeight: 600 }}>
            {lesson.simpleExplanation}
          </div>
        </div>

        {/* COMPREHENSION CHECK (IF APPLICABLE) */}
        {lesson.comprehensionCheck && (
          <div style={{ background: 'rgba(15, 23, 42, 0.8)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '24px', marginTop: '30px' }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#ffffff', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <HelpCircle size={16} color="var(--accent-blue)" />
              <span>Comprehension Check</span>
            </div>

            <div style={{ fontSize: '15px', color: 'var(--text-main)', fontWeight: 600, marginBottom: '16px' }}>
              {lesson.comprehensionCheck.question}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {lesson.comprehensionCheck.options.map((opt) => {
                const isSelected = selectedOption === opt.value;
                const isCorrect = opt.isCorrect;

                let btnBg = 'rgba(2, 6, 23, 0.6)';
                let btnBorder = 'var(--border-color)';

                if (showFeedback && isSelected) {
                  btnBg = isCorrect ? 'rgba(16, 185, 129, 0.25)' : 'rgba(239, 68, 68, 0.25)';
                  btnBorder = isCorrect ? 'var(--accent-green)' : 'var(--accent-red)';
                }

                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleOptionSelect(opt.value)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 18px',
                      borderRadius: 'var(--radius-sm)',
                      background: btnBg,
                      border: `1px solid ${btnBorder}`,
                      color: '#ffffff',
                      fontSize: '14px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <span>{opt.label}</span>
                    {showFeedback && isSelected && (
                      isCorrect ? <Check size={18} color="var(--accent-green)" /> : <X size={18} color="var(--accent-red)" />
                    )}
                  </button>
                );
              })}
            </div>

            {showFeedback && (
              <div style={{ marginTop: '16px', padding: '12px 16px', background: 'rgba(2, 6, 23, 0.8)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)', fontSize: '13px', color: 'var(--text-muted)' }}>
                <strong>Source Explanation:</strong> {lesson.comprehensionCheck.explanation}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Navigation Footer */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px',
          borderTop: '1px solid var(--border-color)',
          paddingTop: '20px',
        }}
      >
        {prevLesson ? (
          <Button
            variant="secondary"
            icon={<ArrowLeft size={16} />}
            onClick={() => onNavigateLesson(prevLesson.id)}
          >
            Previous Lesson
          </Button>
        ) : (
          <div />
        )}

        <div style={{ display: 'flex', gap: '12px' }}>
          {!isCompleted && !lesson.comprehensionCheck && (
            <Button variant="success" icon={<CheckCircle2 size={16} />} onClick={handleMarkRead}>
              Mark Lesson Complete
            </Button>
          )}

          {nextLesson ? (
            <Button
              variant="primary"
              onClick={() => onNavigateLesson(nextLesson.id)}
            >
              <span>Next Lesson</span>
              <ArrowRight size={16} style={{ marginLeft: '6px' }} />
            </Button>
          ) : (
            <Button variant="success" icon={<CheckCircle2 size={16} />} onClick={onBackToOverview}>
              Finish Module Track
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
